/**
 * Starter presets — curated, ready-made dashboard layouts per role.
 *
 * A starter is defined by a `keep` whitelist of widget ids (null = every
 * role-allowed widget). Applying one resets the live layout to the role default
 * and hides everything outside `keep`, so the result is always a valid grid
 * regardless of how the user had previously rearranged things.
 *
 * These are read-only templates shipped with the app — distinct from the
 * personal presets a user saves (those live in the dashboard_presets table).
 */
import {
  DEFAULT_LAYOUT_BY_ROLE,
  widgetsForRole,
} from './widgets/registry';

const ADMIN_HR_STARTERS = [
  {
    id: 'full',
    name: 'Full',
    description: 'Every widget for your role.',
    keep: null,
  },
  {
    id: 'compact',
    name: 'Compact',
    description: 'Core stats and today — fewer extras.',
    keep: [
      'date-time-attendance',
      'total-employees',
      'attendance-summary',
      'performance',
      'personal-notes',
      'upcoming-holidays',
    ],
  },
  {
    id: 'focus',
    name: 'Focus',
    description: 'Just the essentials: today, attendance and notes.',
    keep: ['date-time-attendance', 'attendance-summary', 'personal-notes'],
  },
];

const MEMBER_STARTERS = [
  {
    id: 'full',
    name: 'Full',
    description: 'Every widget for your role.',
    keep: null,
  },
  {
    id: 'compact',
    name: 'Compact',
    description: 'Today, attendance, quick actions and a couple of extras.',
    keep: [
      'date-time-attendance',
      'attendance-summary',
      'performance',
      'member-actions',
      'personal-notes',
      'upcoming-holidays',
    ],
  },
  {
    id: 'focus',
    name: 'Focus',
    description: 'Just the essentials: today, attendance, actions and notes.',
    keep: [
      'date-time-attendance',
      'attendance-summary',
      'member-actions',
      'personal-notes',
    ],
  },
];

export const STARTER_PRESETS_BY_ROLE = {
  admin: ADMIN_HR_STARTERS,
  hr: ADMIN_HR_STARTERS,
  member: MEMBER_STARTERS,
};

export function startersForRole(kind) {
  return STARTER_PRESETS_BY_ROLE[kind] || [];
}

/**
 * Resolve a starter into a concrete { layout, hidden } pair for `kind`.
 * Layout is the full role default (the grid renders + vertically compacts only
 * the visible items); hidden is every role widget not in the keep whitelist.
 */
export function resolveStarter(starter, kind) {
  const all = widgetsForRole(kind).map((w) => w.id);
  const keep = starter.keep == null ? all : starter.keep;
  const keepSet = new Set(keep);
  const hidden = all.filter((id) => !keepSet.has(id));
  const layout = JSON.parse(
    JSON.stringify(DEFAULT_LAYOUT_BY_ROLE[kind] || [])
  );
  return { layout, hidden };
}
